import React, { useState, useEffect, useRef, memo, useCallback } from "react";
import usePresignedUrl from "hooks/usePresignedUrlCache";
import { useImageKey } from "hooks/usePresignedUrlCache";
import tqiIcon from "assets/images/quantumFavNew.svg";
import dtiIcon from "assets/images/dtFavNew.svg";
import spaceIcon from "assets/images/spaceFavNew.svg";
import climateIcon from "assets/images/climateFavNew.svg";
import config from "config";

interface IProps
  extends React.DetailedHTMLProps<
    React.ImgHTMLAttributes<HTMLImageElement>,
    HTMLImageElement
  > {
  src?: string | undefined;
}

const logoPlaceholder =
  config.VERTICAL === "METAVERSE"
    ? dtiIcon
    : config.VERTICAL === "SPACE"
    ? spaceIcon
    : config.VERTICAL === "CLIMATE"
    ? climateIcon
    : tqiIcon;

const SignedImg = ({ alt, crossOrigin, src, style, ...rest }: IProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasIntersected, setHasIntersected] = useState(false);
  const ref = useRef<HTMLImageElement>(null);

  const shouldProcess = src && src !== "-" && src !== "";
  const { imageUrl, loading } = usePresignedUrl(
    isVisible && shouldProcess ? src : null
  );

  const { imageKey } = useImageKey(src);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && !hasIntersected) {
        setIsVisible(true);
        setHasIntersected(true);
      }
    },
    [hasIntersected]
  );

  useEffect(() => {
    if (!ref.current || hasIntersected) return;

    const observer = new IntersectionObserver(handleIntersection, {
      threshold: 0.1,
      rootMargin: "50px",
    });

    observer.observe(ref.current);

    return () => observer.disconnect();
  }, [handleIntersection, hasIntersected]);

  useEffect(() => {
    if (ref.current && imageUrl) {
      const img = ref.current;
      const currentSrc = img.getAttribute("src");
      if (currentSrc && currentSrc !== imageUrl) {
        img.setAttribute("src", "");
        setTimeout(() => {
          img.setAttribute("src", imageUrl);
        }, 10);
      }
    }
  }, [imageUrl]);

  const isShowingPlaceholder = !shouldProcess || loading || !imageUrl;
  const resolvedStyle: React.CSSProperties = {
    ...style,
    ...(isShowingPlaceholder ? { objectFit: "contain" } : {}),
  };

  const imageSrc = React.useMemo(() => {
    if (!shouldProcess) return logoPlaceholder || "";
    if (loading) return logoPlaceholder || "";
    return imageUrl || logoPlaceholder || "";
  }, [shouldProcess, loading, imageUrl]);

  const handleError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      e.currentTarget.src = logoPlaceholder || "";
    },
    []
  );

  if (!shouldProcess) {
    return (
      <img
        key={imageKey}
        ref={ref}
        alt={alt || ""}
        src={logoPlaceholder || ""}
        crossOrigin={crossOrigin || ""}
        style={resolvedStyle}
        {...rest}
      />
    );
  }

  return (
    <img
      key={imageKey}
      ref={ref}
      alt={alt || ""}
      src={imageSrc || src}
      crossOrigin={crossOrigin || ""}
      onError={handleError}
      style={resolvedStyle}
      {...rest}
    />
  );
};

export default memo(SignedImg);
